import type { PartnerReservation, ReservationLookup } from "./types";

export function awaitsAnswer(reservation: PartnerReservation): boolean {
  return reservation.state === "pending_confirmation";
}

/** Only a paid reservation whose termín is still ahead can be called off by the
 *  business — one that already happened is history, not a booking. */
export function canBeCancelled(reservation: PartnerReservation): boolean {
  return reservation.state === "upcoming";
}

/** Only a paid reservation is owed a place at the door; the API refuses the arrival
 *  of any other, so the screen must not invite it. */
export function canRecordArrival(lookup: ReservationLookup): boolean {
  return lookup.state === "upcoming" || lookup.state === "finished";
}

/** Which of the three endings a cancelled reservation had, never how it should
 *  be worded — the wording lives with the UI. */
export type CancellationEnding = "expired" | "business" | "customer" | "unknown";

const endingByReason: Record<string, CancellationEnding> = {
  confirmation_expired: "expired",
  business_reject: "business",
  business_cancel: "business",
  business_cannot_trade: "business",
  customer_storno: "customer",
  checkout_abandoned: "customer",
};

/** A reason the portal does not know and a missing one are the same answer:
 *  an unknown ending. Guessing one would invent the explanation the partner
 *  then repeats to the customer. */
export function cancellationEnding(reason: string | null): CancellationEnding {
  if (reason === null) return "unknown";
  return endingByReason[reason] ?? "unknown";
}

/** A code is read off a customer's phone by someone standing at a door, so
 *  spaces and lower case are typing, not a different code. */
export function normalizeReservationCode(typed: string): string {
  return typed.replace(/\s+/g, "").toUpperCase();
}

export const RESERVATION_CODE_LENGTH = 5;

/** The alphabet the API issues codes from — A–Z and 2–9 without I, O, 0 and 1,
 *  the characters a partner misreads off a phone. Mirrors the database check
 *  `reservations_reservation_code_check`. */
const reservationCodeShape = new RegExp(`^[A-HJ-NP-Z2-9]{${RESERVATION_CODE_LENGTH}}$`);

/** Asks about a code that has already been through `normalizeReservationCode`:
 *  spaces and lower case are typing and are forgiven before this is asked. */
export function isReservationCode(normalized: string): boolean {
  return reservationCodeShape.test(normalized);
}

/** How the deadline should be read, never how it should be worded — the
 *  wording lives with the UI. `lastHour` exists because the final hour must
 *  change the words and not only the colour: colour alone fails in greyscale
 *  and for colour-blind partners. */
export type DeadlineUrgency = "lapsed" | "lastHour" | "sameDay" | "nextDay" | "later";

export interface ConfirmationDeadlineStatus {
  urgency: DeadlineUrgency;
  /** Whole minutes left, clamped at zero. */
  minutesRemaining: number;
}

const MINUTE_MS = 60_000;

function calendarDaysApart(from: Date, to: Date): number {
  const fromMidnight = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const toMidnight = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((toMidnight.getTime() - fromMidnight.getTime()) / (24 * 60 * MINUTE_MS));
}

export function confirmationDeadlineStatus(
  deadline: Date,
  now: Date = new Date(),
): ConfirmationDeadlineStatus {
  const msRemaining = deadline.getTime() - now.getTime();
  const minutesRemaining = Math.max(0, Math.floor(msRemaining / MINUTE_MS));
  // Lapsed means the instant has passed, not "under a minute left" — the
  // answer still goes through in that last minute, and saying otherwise is
  // the distrust this screen cannot afford.
  if (msRemaining <= 0) return { urgency: "lapsed", minutesRemaining: 0 };
  if (minutesRemaining < 60) return { urgency: "lastHour", minutesRemaining };

  const daysApart = calendarDaysApart(now, deadline);
  if (daysApart <= 0) return { urgency: "sameDay", minutesRemaining };
  if (daysApart === 1) return { urgency: "nextDay", minutesRemaining };
  return { urgency: "later", minutesRemaining };
}

export type ReservationHorizon = "upcoming" | "past";

export interface ReservationListView {
  awaitingAnswer: PartnerReservation[];
  upcoming: PartnerReservation[];
  past: PartnerReservation[];
}

/** Admitted by decision, one state at a time: a checkout somebody is still
 *  standing in is not the business's affair until it is paid for, and a state
 *  the API gains later stays invisible until somebody says where it belongs.
 *  These are the API's computed states — a paid reservation reaches the portal
 *  as `upcoming` or `finished`, a rejection as `cancelled`. */
const partnerVisibleStates = new Set(["pending_confirmation", "upcoming", "finished", "cancelled"]);

/** A terminal state is history whatever the termín says: called off is history
 *  the moment it happens, and a finished reservation was declared finished by
 *  the API, so a browser clock a few seconds behind must not put it back among
 *  the upcoming ones. Only the non-terminal rows are divided by the timestamp. */
const settledAsHistoryStates = new Set(["cancelled", "finished"]);

/** A missing instant sorts last in either direction: a row without a termín is
 *  the least urgent thing on the screen, never the most. */
const SORTS_LAST_ASCENDING = Number.POSITIVE_INFINITY;
const SORTS_LAST_DESCENDING = Number.NEGATIVE_INFINITY;

function byDeadline(a: PartnerReservation, b: PartnerReservation): number {
  const deadlineOf = (reservation: PartnerReservation) =>
    reservation.confirmDeadline?.getTime() ?? SORTS_LAST_ASCENDING;
  return deadlineOf(a) - deadlineOf(b);
}

function bySoonestStart(a: PartnerReservation, b: PartnerReservation): number {
  const startOf = (reservation: PartnerReservation) =>
    reservation.startTime?.getTime() ?? SORTS_LAST_ASCENDING;
  return startOf(a) - startOf(b);
}

function byMostRecentStart(a: PartnerReservation, b: PartnerReservation): number {
  const startOf = (reservation: PartnerReservation) =>
    reservation.startTime?.getTime() ?? SORTS_LAST_DESCENDING;
  return startOf(b) - startOf(a);
}

export function partitionReservations(
  all: PartnerReservation[],
  now: Date,
): ReservationListView {
  const worthShowing = all.filter((reservation) => partnerVisibleStates.has(reservation.state));
  const answered = worthShowing.filter((reservation) => !awaitsAnswer(reservation));
  const hasHappened = (reservation: PartnerReservation) =>
    settledAsHistoryStates.has(reservation.state) ||
    (reservation.startTime !== null && reservation.startTime.getTime() <= now.getTime());

  return {
    awaitingAnswer: worthShowing.filter(awaitsAnswer).sort(byDeadline),
    upcoming: answered.filter((reservation) => !hasHappened(reservation)).sort(bySoonestStart),
    past: answered.filter(hasHappened).sort(byMostRecentStart),
  };
}

/** The offer a partner arrived from, kept as a filter over the whole screen
 *  rather than a section of its own: the rows for one sleva are still a queue,
 *  a list of what is ahead and a list of what has happened. */
export function filterReservationViewByOffer(
  view: ReservationListView,
  offerId: number | null,
): ReservationListView {
  if (offerId === null) return view;
  const belongsToOffer = (reservation: PartnerReservation) => reservation.offerId === offerId;
  return {
    awaitingAnswer: view.awaitingAnswer.filter(belongsToOffer),
    upcoming: view.upcoming.filter(belongsToOffer),
    past: view.past.filter(belongsToOffer),
  };
}

/** The listed states an offer's `reserved` counts too. A cancellation gives its
 *  place back; `finished` is a paid reservation whose termín has passed and
 *  keeps its place in the count. */
const statesHoldingAPlace = new Set(["pending_confirmation", "upcoming", "finished"]);

/** How many places the offer counts as taken that this list never shows — a
 *  checkout still in flight holds one and stays hidden (see
 *  `partnerVisibleStates`). Derived rather than asked for: the API publishes
 *  the total only, and the difference is not a field on the offer. */
export function unfinishedCheckoutCount(view: ReservationListView, reserved: number): number {
  const listedPlaces = [...view.awaitingAnswer, ...view.upcoming, ...view.past].filter(
    (reservation) => statesHoldingAPlace.has(reservation.state),
  ).length;
  return Math.max(0, reserved - listedPlaces);
}

/** One field for the whole screen: a partner types a name to find a person,
 *  not a section, so every section narrows at once. */
export function filterReservationView(
  view: ReservationListView,
  term: string,
): ReservationListView {
  const matchesTerm = (reservation: PartnerReservation) => matchesSearch(reservation, term);
  return {
    awaitingAnswer: view.awaitingAnswer.filter(matchesTerm),
    upcoming: view.upcoming.filter(matchesTerm),
    past: view.past.filter(matchesTerm),
  };
}

function fold(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

/** Folded on both sides because a partner types „novak" for „Nováková" and
 *  reads a kód off a customer's phone in whatever case it is shown. */
export function matchesSearch(reservation: PartnerReservation, term: string): boolean {
  const needle = fold(term);
  if (needle === "") return true;
  return (
    fold(reservation.customerName ?? "").includes(needle) ||
    fold(reservation.code ?? "").includes(needle)
  );
}
